import { type Dayjs } from "dayjs"

import i18n from "../../../../../../../i18n.ts"

export type AxisUnit = "day" | "week" | "month" | "quarter" | "year"
export type ZoomLevel = "fit" | "day" | "week" | "month" | "quarter" | "year"
/** A named level, or pixels per day after a wheel or pinch zoom. */
export type GanttZoom = ZoomLevel | number

export interface GanttRange {
  start: Dayjs
  end: Dayjs
}

export interface AxisTick {
  /** The unit's own first day — before `offsetDays` when the tick is clipped. */
  start: Dayjs
  /** Days from the range start to where this tick's visible part begins. */
  offsetDays: number
  /** Visible days — a tick clipped by either end of the range is shorter. */
  widthDays: number
  /** Short text for the tick row ("Q3", "Aug", "14"). */
  label: string
  /** Unambiguous text for a tooltip or a band ("Q3 2026", "August 2026"). */
  title: string
}

export const ZOOM_LEVELS: ZoomLevel[] = [
  "fit",
  "day",
  "week",
  "month",
  "quarter",
  "year"
]

// A year at the floor is still 43.8px — the coarsest label always fits.
export const MIN_DAY_WIDTH = 0.12
export const MAX_DAY_WIDTH = 90
export const MIN_LABEL_PX = 40

// Beyond roughly six months the whole schedule is the useful first view.
const FIT_BY_DEFAULT_AFTER_DAYS = 180

// Each threshold is the lowest px/day at which the unit's shortest member
// still clears MIN_LABEL_PX: Feb (28 days) needs 1.45, Q1 (90 days) 0.45.
const AXIS_THRESHOLDS: [number, AxisUnit][] = [
  [40, "day"],
  [6, "week"],
  [1.45, "month"],
  [0.45, "quarter"]
]

const LEVEL_DAY_WIDTH: Record<Exclude<ZoomLevel, "fit">, number> = {
  // Over the 40px a day's own tick needs, so Day reads one tick per day.
  day: 44,
  week: 20,
  month: 4,
  quarter: 1.2,
  year: 0.35
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max)

export const axisUnitFor = (dayWidth: number): AxisUnit =>
  AXIS_THRESHOLDS.find(([min]) => dayWidth >= min)?.[1] ?? "year"

export const bandUnitFor = (unit: AxisUnit): AxisUnit | null =>
  ({
    day: "month",
    week: "month",
    month: "quarter",
    quarter: "year",
    year: null
  })[unit] as AxisUnit | null

const quarterOf = (date: Dayjs) => Math.floor(date.month() / 3) + 1

const startOfUnit = (date: Dayjs, unit: AxisUnit): Dayjs => {
  if (unit === "quarter") {
    return date.startOf("month").month(Math.floor(date.month() / 3) * 3)
  }
  return date.startOf(unit)
}

const nextOfUnit = (date: Dayjs, unit: AxisUnit): Dayjs =>
  unit === "quarter" ? date.add(3, "month") : date.add(1, unit)

type Translate = (key: string, options?: Record<string, unknown>) => string

const appTranslate: Translate = (key, options) => i18n.t(key, options)

const tickText = (
  start: Dayjs,
  unit: AxisUnit,
  t: Translate
): { label: string; title: string } => {
  switch (unit) {
    case "day":
      return {
        label: start.format("D"),
        title: start.format("ddd, MMM D, YYYY")
      }
    case "week":
      return {
        label: start.format("MMM D"),
        title: t("milestonesGantt.axis.weekOf", {
          date: start.format("MMM D, YYYY")
        })
      }
    case "month":
      return { label: start.format("MMM"), title: start.format("MMMM YYYY") }
    case "quarter":
      return {
        label: t("milestonesGantt.axis.quarter", { quarter: quarterOf(start) }),
        title: t("milestonesGantt.axis.quarterOfYear", {
          quarter: quarterOf(start),
          year: start.year()
        })
      }
    case "year":
      return { label: start.format("YYYY"), title: start.format("YYYY") }
  }
}

/** One tick per calendar unit touching the range, cut to the range at both ends. */
export function buildAxisTicks(
  range: GanttRange,
  unit: AxisUnit,
  t: Translate = appTranslate
): AxisTick[] {
  const rangeStart = range.start.startOf("day")
  const endExclusive = range.end.startOf("day").add(1, "day")
  const ticks: AxisTick[] = []
  let cursor = startOfUnit(rangeStart, unit)
  while (cursor.isBefore(endExclusive)) {
    const next = nextOfUnit(cursor, unit)
    const visibleStart = cursor.isBefore(rangeStart) ? rangeStart : cursor
    const visibleEnd = next.isAfter(endExclusive) ? endExclusive : next
    ticks.push({
      start: cursor,
      offsetDays: visibleStart.diff(rangeStart, "day"),
      widthDays: visibleEnd.diff(visibleStart, "day"),
      ...tickText(cursor, unit, t)
    })
    cursor = next
  }
  return ticks
}

// A tick's text is only drawn when it has room; the thresholds above make
// that true of every full tick, so only a clipped end tick goes blank.
export const tickHasRoom = (tick: AxisTick, dayWidth: number) =>
  tick.widthDays * dayWidth >= MIN_LABEL_PX

// "Aug 23 – 29" within a month, "Aug 30 – Sep 5" across one.
export const formatWeekRange = (start: Dayjs, end: Dayjs) =>
  start.month() === end.month()
    ? `${start.format("MMM D")} – ${end.format("D")}`
    : `${start.format("MMM D")} – ${end.format("MMM D")}`

const WEEK_RANGE_MIN_PX = 96
const FULL_MONTH_MIN_PX = 90

/** The text a tick shows at this scale, or null when it has no room for any. */
export function tickDisplayLabel(
  tick: AxisTick,
  unit: AxisUnit,
  dayWidth: number
): string | null {
  const px = tick.widthDays * dayWidth
  if (px < MIN_LABEL_PX) return null
  if (unit === "week" && px >= WEEK_RANGE_MIN_PX)
    return formatWeekRange(tick.start, tick.start.add(6, "day"))
  if (unit === "month" && px >= FULL_MONTH_MIN_PX)
    return tick.start.format("MMMM")
  return tick.label
}

export const fitDayWidth = (totalDays: number, availableWidth: number) =>
  availableWidth / Math.max(totalDays, 1)

/**
 * Pixels per day for a zoom. `ceiling` is the surface's own cap for Fit, so a
 * two-day project is not stretched into 300px columns.
 */
export function resolveDayWidth(
  zoom: GanttZoom,
  totalDays: number,
  availableWidth: number,
  ceiling: number
): number {
  if (typeof zoom === "number") return clamp(zoom, MIN_DAY_WIDTH, MAX_DAY_WIDTH)
  if (zoom === "fit") {
    return clamp(
      fitDayWidth(totalDays, availableWidth),
      MIN_DAY_WIDTH,
      Math.min(ceiling, MAX_DAY_WIDTH)
    )
  }
  return LEVEL_DAY_WIDTH[zoom]
}

export const defaultZoomFor = (
  totalDays: number,
  availableWidth: number
): ZoomLevel =>
  totalDays > FIT_BY_DEFAULT_AFTER_DAYS ||
  fitDayWidth(totalDays, availableWidth) >= LEVEL_DAY_WIDTH.week
    ? "fit"
    : "week"

// deltaY ±100 (one mouse-wheel notch) is about a 22% step; a trackpad pinch
// sends many small deltas, so the same curve reads as continuous there.
const WHEEL_SENSITIVITY = 0.0025

export const wheelZoom = (dayWidth: number, deltaY: number) =>
  clamp(
    dayWidth * Math.exp(-deltaY * WHEEL_SENSITIVITY),
    MIN_DAY_WIDTH,
    MAX_DAY_WIDTH
  )

/**
 * The scroll position that keeps the date under `pointerX` (px from the left
 * edge of the visible chart) in place after the scale changes.
 */
export function anchoredScrollLeft({
  scrollLeft,
  pointerX,
  dayWidth,
  nextDayWidth
}: {
  scrollLeft: number
  pointerX: number
  dayWidth: number
  nextDayWidth: number
}): number {
  const dayUnderPointer = (scrollLeft + pointerX) / dayWidth
  return Math.max(0, dayUnderPointer * nextDayWidth - pointerX)
}

/** Kept between the visible chart's left edge and the first bar on "Start". */
export const JUMP_START_MARGIN_PX = 24

/**
 * The scroll offset that puts chart x `targetX` `lead` px in from the
 * visible chart's left edge, never before the chart starts.
 */
export const scrollLeftToShow = (targetX: number, lead: number): number =>
  Math.max(0, Math.round(targetX - lead))

export const zoomLevelFor = (zoom: GanttZoom): ZoomLevel | null =>
  typeof zoom === "number" ? null : zoom

export function parseStoredZoom(value: unknown): GanttZoom | null {
  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0 ? value : null
  }
  return typeof value === "string" && (ZOOM_LEVELS as string[]).includes(value)
    ? (value as ZoomLevel)
    : null
}
